/**
 * Client-side JavaScript: visual regression section.
 *
 * Renders one card per snapshot comparison, with up to four ways of looking at
 * the same pair of images: side by side, a wipe slider, an onion-skin blend and
 * Playwright's own diff. Which modes appear depends on which images exist — a
 * passing comparison has only a baseline, and a failing one has all four.
 *
 * The markup reuses the report's existing `.seg-ctrl` / `.seg-btn` / `.seg-pane`
 * convention, so the delegated pane-switch handler in `html-js-interactions`
 * drives the tabs with no changes there. Only the two range inputs need a
 * listener of their own, registered at the bottom of this module.
 *
 * ES5-compatible vanilla JS — it is concatenated into the report's single
 * inline script, under a CSP that allows no external resources.
 */

export const JS_VISUAL = `
  /* ── Visual Regression ── */

  function visStatusLabel(status){
    if(status==='new')return 'new baseline';
    if(status==='updated')return 'baseline updated';
    return status;
  }

  function visMetricsText(v){
    var parts=[];
    if(typeof v.diffPixels==='number')parts.push(v.diffPixels.toLocaleString()+' px');
    if(typeof v.diffPixelRatio==='number')parts.push((v.diffPixelRatio*100).toFixed(2)+'%');
    if(v.width&&v.height){
      var dims=v.width+'x'+v.height;
      /* Call out a size change explicitly: a comparison can fail purely
         because the render changed shape, and the pixel count alone reads
         like a content change when that happens. */
      if(v.actualWidth&&v.actualHeight&&(v.actualWidth!==v.width||v.actualHeight!==v.height))
        dims+=' -> '+v.actualWidth+'x'+v.actualHeight;
      parts.push(dims);
    }
    return parts.join(' \\u00b7 ');
  }

  /* Deliberately no loading="lazy" on any image below. Every pane but the
     first is built inside a display:none container, and Chrome does not
     reliably start a lazy load when such an image is later revealed — the
     diff pane came up blank every time. A test has a handful of comparisons
     at most and they are only built when the drawer opens, so eager loading
     is by far the cheaper mistake. */
  function visImg(src,alt){
    return '<img class="artifact-col-img" src="'+esc(src)+'" alt="'+esc(alt)+'">';
  }

  function visSideBySide(v){
    var h='<div class="vis-pair">';
    if(v.baseline)h+='<div><div class="vis-pane-label">Baseline</div>'+visImg(v.baseline,'Baseline')+'</div>';
    if(v.actual)h+='<div><div class="vis-pane-label">This run</div>'+visImg(v.actual,'This run')+'</div>';
    if(v.previous)h+='<div><div class="vis-pane-label">Previous attempt</div>'+visImg(v.previous,'Previous attempt')+'</div>';
    return h+'</div>';
  }

  function visSlider(v,id){
    var h='<div class="vis-stack" data-vis-stack="'+id+'">';
    h+='<img src="'+esc(v.baseline)+'" alt="Baseline">';
    h+='<img class="vis-top" data-vis-wipe="'+id+'" src="'+esc(v.actual)+'" alt="This run" style="clip-path:inset(0 0 0 50%)">';
    h+='<div class="vis-handle" data-vis-handle="'+id+'" style="left:50%"></div>';
    h+='</div>';
    h+='<div class="vis-range-row"><span>Baseline</span><input type="range" class="vis-range" min="0" max="100" value="50" data-vis-slider="'+id+'" aria-label="Wipe between baseline and this run"><span>This run</span></div>';
    return h;
  }

  function visOnion(v,id){
    var h='<div class="vis-stack">';
    h+='<img src="'+esc(v.baseline)+'" alt="Baseline">';
    h+='<img class="vis-top" data-vis-fade="'+id+'" src="'+esc(v.actual)+'" alt="This run" style="opacity:0.5">';
    h+='</div>';
    h+='<div class="vis-range-row"><span>Baseline</span><input type="range" class="vis-range" min="0" max="100" value="50" data-vis-onion="'+id+'" aria-label="Blend between baseline and this run"><span>This run</span></div>';
    return h;
  }

  function visModes(v){
    var modes=[];
    if(v.baseline&&v.actual){
      modes.push({key:'side',label:'Side by side'});
      modes.push({key:'wipe',label:'Wipe'});
      modes.push({key:'onion',label:'Onion skin'});
    }else if(v.baseline||v.actual){
      modes.push({key:'side',label:'Image'});
    }
    if(v.diff)modes.push({key:'diff',label:'Pixel diff'});
    /* Only when a DOM baseline existed to compare against — an assertion
       written with Playwright's own expect compares pixels only. */
    if(v.domChangeCount!==undefined)modes.push({key:'dom',label:'Elements'});
    return modes;
  }

  function visPaneBody(mode,v,id){
    if(mode==='side')return visSideBySide(v);
    if(mode==='wipe')return visSlider(v,id);
    if(mode==='onion')return visOnion(v,id);
    if(mode==='dom')return renderDomPane(v);
    return '<div class="vis-diff-wrap">'+visImg(v.diff,'Pixel diff')+'</div>';
  }

  function renderVisualCard(v,idx){
    var id='v'+idx;
    var h='<div class="vis-card">';
    h+='<div class="vis-head"><span class="vis-name">'+esc(v.name)+'</span>';
    h+='<span class="vis-badge vb-'+esc(v.status)+'">'+esc(visStatusLabel(v.status))+'</span>';
    if(v.domChangeCount)h+='<span class="vis-badge vb-dom">'+v.domChangeCount+' element'+(v.domChangeCount===1?'':'s')+'</span>';
    var metrics=visMetricsText(v);
    if(metrics)h+='<span class="vis-metrics">'+esc(metrics)+'</span>';
    h+='</div>';

    var modes=visModes(v);
    if(modes.length===0){
      h+='<div class="vis-note">No images were captured for this comparison.</div></div>';
      return h;
    }

    if(v.status==='new')h+='<div class="vis-note">No baseline existed, so this render was written as one. Nothing was compared.</div>';
    else if(v.status==='updated')h+='<div class="vis-note">Run with --update-snapshots: the baseline was rewritten rather than compared.</div>';

    if(modes.length>1){
      h+='<div class="seg-ctrl" data-seg-group="visual-'+id+'">';
      for(var m=0;m<modes.length;m++){
        h+='<button class="seg-btn'+(m===0?' active':'')+'" data-seg="'+id+'-'+modes[m].key+'">'+esc(modes[m].label)+'</button>';
      }
      h+='</div>';
    }
    for(var p=0;p<modes.length;p++){
      h+='<div class="seg-pane'+(p===0?' active':'')+'" data-seg-pane="'+id+'-'+modes[p].key+'">'+visPaneBody(modes[p].key,v,id)+'</div>';
    }
    return h+'</div>';
  }

  function renderVisualSection(visual){
    if(!visual||visual.length===0)return '';
    var failed=0;
    for(var f=0;f<visual.length;f++)if(visual[f].status==='failed')failed++;
    var h='<div class="vis-header"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18"/></svg>Visual comparisons <span class="vis-count">'+visual.length+'</span>';
    /* A separator, because two bare adjacent numbers read as one. */
    if(failed>0)h+='<span class="vis-count vis-count-fail">· '+failed+' failed</span>';
    h+='</div><div class="vis-list">';
    for(var i=0;i<visual.length;i++)h+=renderVisualCard(visual[i],i);
    return h+'</div>';
  }

  /* Slider and onion-skin are driven by range inputs, which fire 'input'
     rather than 'click' — the delegated click handler cannot see them. One
     document-level listener covers every card, including cards rendered into
     the drawer long after this runs. */
  document.addEventListener('input',function(e){
    var el=e.target;
    if(!el||!el.getAttribute)return;

    var wipeId=el.getAttribute('data-vis-slider');
    if(wipeId){
      var card=el.closest('.vis-card');
      if(!card)return;
      var top=card.querySelector('[data-vis-wipe="'+wipeId+'"]');
      var handle=card.querySelector('[data-vis-handle="'+wipeId+'"]');
      if(top)top.style.clipPath='inset(0 0 0 '+el.value+'%)';
      if(handle)handle.style.left=el.value+'%';
      return;
    }

    var fadeId=el.getAttribute('data-vis-onion');
    if(fadeId){
      var ocard=el.closest('.vis-card');
      if(!ocard)return;
      var otop=ocard.querySelector('[data-vis-fade="'+fadeId+'"]');
      if(otop)otop.style.opacity=String(el.value/100);
    }
  });
`;
